import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiEye, FiGrid, FiCheckCircle, FiXCircle, FiToggleLeft, FiToggleRight, FiSearch } from 'react-icons/fi';
import { campaignsApi, getImageUrl } from '../../utils/api';
import '../admin/AdminPages.css';

const MyCampaignsPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    ['my-campaigns', page, search],
    () => campaignsApi.getMy({ page, limit: 50, search }),
    { keepPreviousData: true }
  );

  const toggleStatusMutation = useMutation(
    ({ id, isOpen }) => campaignsApi.update(id, { isOpenForApplications: isOpen }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries('my-campaigns');
        toast.success(variables.isOpen ? 'הקמפיין נפתח להרשמה' : 'הקמפיין נסגר להרשמה');
      },
      onError: () => {
        toast.error('שגיאה בעדכון הקמפיין');
      }
    }
  );

  const campaigns = data?.data?.campaigns || [];
  const pagination = data?.data?.pagination || {};

  const isOpen = (campaign) => campaign.isOpenForApplications || campaign.is_open_for_applications;

  const getStatusBadge = (campaign) => {
    if (isOpen(campaign)) {
      return <span className="badge badge-success"><FiCheckCircle /> פתוח</span>;
    } else {
      return <span className="badge badge-error"><FiXCircle /> סגור</span>;
    }
  };

  const handleToggleStatus = (campaign) => {
    const newStatus = !isOpen(campaign);
    toggleStatusMutation.mutate({ id: campaign.id, isOpen: newStatus });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="header-content">
          <h1>הקמפיינים שלי</h1>
          <span className="total-count">{pagination.total || 0} קמפיינים</span>
        </div>
        <Link to="/dashboard/campaigns/create" className="btn btn-primary">
          <FiPlus /> קמפיין חדש
        </Link>
      </header>

      {/* Toolbar */}
      <div className="admin-toolbar">
        <div className="search-form">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="חיפוש קמפיין..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>טוען נתונים...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>שגיאה בטעינת הנתונים</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state">
          <FiGrid className="empty-icon" />
          <p>עדיין אין לך קמפיינים</p>
          <Link to="/dashboard/campaigns/create" className="btn btn-primary">
            <FiPlus /> צור קמפיין ראשון
          </Link>
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>שם הקמפיין</th>
                <th>סטטוס</th>
                <th>פתיחה/סגירה</th>
                <th>תאריך יצירה</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td data-label="שם הקמפיין">
                    <div className="campaign-cell">
                      {(campaign.cover_image_url || campaign.coverImage) && (
                        <img
                          src={getImageUrl(campaign.cover_image_url || campaign.coverImage)}
                          alt={campaign.title}
                          className="campaign-thumb"
                        />
                      )}
                      <span>{campaign.title}</span>
                    </div>
                  </td>
                  <td data-label="סטטוס">{getStatusBadge(campaign)}</td>
                  <td data-label="פתיחה/סגירה">
                    <button
                      className={`toggle-switch ${isOpen(campaign) ? 'active' : ''}`}
                      onClick={() => handleToggleStatus(campaign)}
                      disabled={toggleStatusMutation.isLoading}
                    >
                      <span className="toggle-slider"></span>
                      <span className={`toggle-label ${isOpen(campaign) ? 'open' : 'closed'}`}>
                        {isOpen(campaign) ? 'פתוח' : 'סגור'}
                      </span>
                    </button>
                  </td>
                  <td data-label="תאריך יצירה">{formatDate(campaign.createdAt)}</td>
                  <td data-label="פעולות">
                    <div className="actions-cell compact">
                      <Link
                        to={`/dashboard/campaigns/${campaign.id}/edit`}
                        className="btn-icon"
                        title="ערוך"
                      >
                        <FiEdit2 />
                      </Link>
                      <Link
                        to={`/campaigns/${campaign.id}`}
                        className="btn-icon"
                        title="צפה"
                      >
                        <FiEye />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            הקודם
          </button>
          <span className="page-info">
            עמוד {page} מתוך {pagination.pages}
          </span>
          <button
            className="btn btn-outline"
            disabled={page === pagination.pages}
            onClick={() => setPage(page + 1)}
          >
            הבא
          </button>
        </div>
      )}
    </div>
  );
};

export default MyCampaignsPage;
